/**
 * Dashboard — the screen an operator lives on.
 *
 * The layout is ordered by what a shift actually needs, in order: is money
 * behaving (hold vs edge), is anything waiting on me (approvals), and how much
 * business is there (turnover, players). Those first two are the LEAD tiles —
 * display-size figures on a lifted surface — and the rest are deliberately
 * quieter. Six tiles shouting equally is the same as none of them shouting.
 *
 * The hold report is the centre of it, and it is the one feature card on the
 * screen. Realised hold is what the house actually kept; configured edge is what
 * the payout table was designed to keep. They should converge as volume grows. A
 * game that stays above its edge is usually a payout table with a rounding error
 * in the house's favour — a regulatory and reputational problem. A game that
 * stays below it is either a mispriced table or a player working out something we
 * have not. Either way the drift is the signal, which is why the table sorts by
 * drift rather than by revenue, carries a severity gutter down its left edge, and
 * renders the drift on a diverging meter where direction reads before value.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { fetchGameHold, fetchOverview } from '../api/client.ts';
import type { GameHold, MetricsWindow } from '../api/types.ts';
import { useAsync } from '../lib/useAsync.ts';
import { cmp, format, formatCompact, formatPercent, sub, toUnits } from '../lib/money.ts';
import { shortTime } from '../lib/datetime.ts';
import { navigate } from '../lib/router.ts';
import {
  Card,
  Delta,
  EmptyState,
  ErrorState,
  HoldDrift,
  Money,
  Stat,
  TableSkeleton,
  driftLevel,
  driftOf,
  DRIFT_ALERT,
  DRIFT_WATCH,
} from '../ui/primitives.tsx';
import {
  IconAlert,
  IconChevron,
  IconGauge,
  IconInbound,
  IconPeople,
  IconSliders,
  IconTrend,
} from '../ui/icons.tsx';

const WINDOWS: { id: MetricsWindow; label: string }[] = [
  { id: '24h', label: '24 hours' },
  { id: '7d', label: '7 days' },
  { id: '30d', label: '30 days' },
];

/** The hold table's shape, reused by the loading skeleton so nothing jumps. */
const HOLD_COLUMNS = [
  'text',
  'text',
  'num',
  'num',
  'num',
  'num',
  'num',
  'num',
  'num',
  'num',
] as const;

export function Dashboard(): ReactNode {
  const [window, setWindow] = useState<MetricsWindow>('24h');

  const overview = useAsync(() => fetchOverview(window), [window]);
  const hold = useAsync(() => fetchGameHold(window), [window]);

  /**
   * Sorted by drift magnitude, largest first — the whole point of the table is
   * that the worst-behaved game is at the top without anyone looking for it.
   * Games with no turnover sink to the bottom: an undefined hold is not a
   * finding.
   */
  const rows = useMemo(() => {
    const list = [...(hold.data ?? [])];
    list.sort((a, b) => {
      const da = driftOf(a.realisedHoldPct, a.configuredEdgePct);
      const db = driftOf(b.realisedHoldPct, b.configuredEdgePct);
      if (da === null && db === null) return 0;
      if (da === null) return 1;
      if (db === null) return -1;
      return cmp(magnitude(db), magnitude(da));
    });
    return list;
  }, [hold.data]);

  const alerting = rows.filter(
    (r) => driftLevel(driftOf(r.realisedHoldPct, r.configuredEdgePct)) === 'alert',
  );
  const watching = rows.filter(
    (r) => driftLevel(driftOf(r.realisedHoldPct, r.configuredEdgePct)) === 'watch',
  );

  const data = overview.data;
  const headlineDrift =
    data && data.realisedHoldPct !== null && data.configuredEdgePct !== null
      ? sub(data.realisedHoldPct, data.configuredEdgePct)
      : null;
  const headlineLevel = driftLevel(headlineDrift);
  const pending = data ? data.pendingDeposits + data.pendingWithdrawals : 0;

  return (
    <div className="stack">
      <div className="toolbar">
        <div className="segmented" role="group" aria-label="Reporting window">
          {WINDOWS.map((w) => (
            <button
              key={w.id}
              type="button"
              className={`segment${window === w.id ? ' is-active' : ''}`}
              aria-pressed={window === w.id}
              onClick={() => setWindow(w.id)}
            >
              {w.label}
            </button>
          ))}
        </div>
        <span className="card__spacer" />
        {data ? (
          <span className="field__hint num">
            {shortTime(data.windowFrom)} → {shortTime(data.windowTo)}
          </span>
        ) : null}
      </div>

      {alerting.length > 0 ? (
        <div className="banner banner--danger">
          <span className="banner__icon">
            <IconAlert />
          </span>
          <div className="banner__body">
            <div className="banner__title">
              {alerting.length === 1
                ? `1 game is more than ${DRIFT_ALERT}pp off its configured edge`
                : `${alerting.length} games are more than ${DRIFT_ALERT}pp off their configured edge`}
            </div>
            <ul className="banner__list">
              {alerting.map((g) => (
                <li key={g.slug}>
                  <strong>{g.name}</strong> — realised {formatPercent(g.realisedHoldPct)} against a
                  configured {formatPercent(g.configuredEdgePct)} on{' '}
                  <Money value={g.turnover} currency /> of turnover across{' '}
                  {g.betCount.toLocaleString()} bets. Check the payout table before it is checked
                  for you.
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}

      {overview.error ? (
        <div className="banner banner--warn">
          <span className="banner__icon">
            <IconAlert />
          </span>
          <div className="banner__body">
            <div className="banner__title">The overview figures did not load</div>
            {overview.error}
          </div>
        </div>
      ) : null}

      {/* The two readings a shift is run from. */}
      <div className="stats stats--lead">
        <Stat
          lead
          icon={<IconGauge size="sm" />}
          label="Realised hold"
          value={data ? formatPercent(data.realisedHoldPct) : '—'}
          tone={headlineLevel === 'alert' ? 'loss' : headlineLevel === 'watch' ? 'pending' : undefined}
          trailing={
            headlineDrift === null ? null : (
              <Delta
                value={headlineDrift}
                level={headlineLevel === 'none' ? 'ok' : headlineLevel}
              />
            )
          }
          note={
            data ? (
              <>
                against a configured {formatPercent(data.configuredEdgePct)} · {rows.length} games
              </>
            ) : (
              'no turnover in this window'
            )
          }
        />
        <Stat
          lead
          icon={<IconInbound size="sm" />}
          label="Waiting on you"
          value={data ? pending.toLocaleString() : '—'}
          tone={pending > 0 ? 'pending' : undefined}
          note={
            data ? (
              <>
                {data.pendingDeposits} deposits · {data.pendingWithdrawals} withdrawals holding{' '}
                <Money value={data.pendingWithdrawalAmount} currency />
              </>
            ) : (
              ''
            )
          }
        />
      </div>

      <div className="stats">
        <Stat
          icon={<IconTrend size="sm" />}
          label="Turnover"
          value={data ? formatCompact(data.turnover) : '—'}
          note={data ? `${data.betCount.toLocaleString()} bets` : ''}
          title={data ? format(data.turnover, { places: 4 }) : ''}
        />
        <Stat
          label="Gross gaming revenue"
          value={data ? formatCompact(data.grossGamingRevenue) : '—'}
          tone={data && toUnits(data.grossGamingRevenue) < 0n ? 'loss' : 'win'}
          note="turnover less payouts"
          title={data ? format(data.grossGamingRevenue, { places: 4 }) : ''}
        />
        <Stat
          icon={<IconPeople size="sm" />}
          label="Active players"
          value={data ? data.activePlayers.toLocaleString() : '—'}
          note={data ? `${data.newPlayers.toLocaleString()} new` : ''}
        />
        <Stat
          label="Deposits in"
          value={data ? formatCompact(data.depositsTotal) : '—'}
          note={data ? <>out: <Money value={data.withdrawalsTotal} currency /></> : ''}
          title={data ? format(data.depositsTotal, { places: 4 }) : ''}
        />
      </div>

      <Card
        feature
        flush
        icon={<IconGauge size="lg" />}
        title="Realised hold vs configured edge"
        sub={
          rows.length === 0
            ? 'Sorted by drift, worst first.'
            : `Sorted by drift, worst first · ${alerting.length} alerting · ${watching.length} to watch`
        }
        actions={
          <button type="button" className="btn btn--sm btn--ghost" onClick={() => navigate('games')}>
            <IconSliders size="sm" />
            Game settings
            <IconChevron size="sm" />
          </button>
        }
        footer={rows.length > 0 ? <DriftLegend /> : null}
      >
        {hold.error && rows.length === 0 ? (
          <ErrorState
            title="The hold report did not load"
            detail={hold.error}
            onRetry={hold.reload}
          />
        ) : hold.loading && rows.length === 0 ? (
          <TableSkeleton columns={HOLD_COLUMNS} rows={8} />
        ) : rows.length === 0 ? (
          <EmptyState
            title="No game has taken a bet in this window"
            hint="Widen the window, or check that the round engine is running. This is the only screen that would notice."
          />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th className="gutter" aria-label="Severity" />
                  <th>Game</th>
                  <th className="right">Turnover</th>
                  <th className="right">Payouts</th>
                  <th className="right">GGR</th>
                  <th className="right col-group">Configured</th>
                  <th className="right">Realised</th>
                  <th className="right">Drift</th>
                  <th className="right col-group">Bets</th>
                  <th className="right">Players</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <HoldRow key={row.slug} row={row} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

/**
 * The scale, stated. A meter without a legend is a decoration: an operator has
 * to be told once where amber starts before the colour means anything.
 */
function DriftLegend(): ReactNode {
  return (
    <div className="legend">
      <span className="legend__item">
        <span className="legend__swatch legend__swatch--ok" />
        under {DRIFT_WATCH}pp — normal variance
      </span>
      <span className="legend__item">
        <span className="legend__swatch legend__swatch--watch" />
        {DRIFT_WATCH}–{DRIFT_ALERT}pp — watch it
      </span>
      <span className="legend__item">
        <span className="legend__swatch legend__swatch--alert" />
        over {DRIFT_ALERT}pp — check the payout table
      </span>
      <span className="card__spacer" />
      <span className="legend__item">
        Meter fills left of centre when the game holds under its edge, right when it holds over.
      </span>
    </div>
  );
}

function HoldRow({ row }: { row: GameHold }): ReactNode {
  const level = driftLevel(driftOf(row.realisedHoldPct, row.configuredEdgePct));

  return (
    <tr>
      <td className="gutter">
        <span
          className={`gutter__bar${level === 'alert' || level === 'watch' ? ` gutter__bar--${level}` : ''}`}
        />
      </td>
      <td className="nowrap cell-lead">
        {row.name}
        {row.enabled ? null : <span className="dim"> · disabled</span>}
        <span className="cell-sub">{row.slug}</span>
      </td>
      <td className="right mono">
        <Money value={row.turnover} />
      </td>
      <td className="right mono dim">
        <Money value={row.payouts} />
      </td>
      <td className="right mono">
        <Money value={row.grossGamingRevenue} colour />
      </td>
      <td className="right mono dim col-group">{formatPercent(row.configuredEdgePct)}</td>
      <td className="right mono">{formatPercent(row.realisedHoldPct)}</td>
      <td className="right">
        <HoldDrift realised={row.realisedHoldPct} configured={row.configuredEdgePct} />
      </td>
      <td className="right mono dim col-group">{row.betCount.toLocaleString()}</td>
      <td className="right mono dim">{row.playerCount.toLocaleString()}</td>
    </tr>
  );
}

function magnitude(value: string): string {
  return value.startsWith('-') ? value.slice(1) : value;
}
